'use client';

import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  AcademicCapIcon, 
  PlusIcon, 
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { 
  APExam, 
  APCreditEquivalent, 
  UserAPCredit,
  RutgersCourse, 
  getBaseID
} from '@/app/types/interfaces';
import { 
  getAllAPExams, 
  getAPExamById 
} from '@/app/lib/apCreditData';

interface APCreditInputProps {
  onAPCreditsChange: (apCredits: UserAPCredit[]) => void;
  completedCourses: RutgersCourse[];
  // Provide any already-saved credits so the component can hydrate on mount
  initialAPCredits?: UserAPCredit[];
  // Pass baseId string; caller will resolve to RutgersCourse
  onAddCompletedCourse?: (courseBaseId: string) => void;
}

export default function APCreditInput({ 
  onAPCreditsChange, 
  initialAPCredits,
}: APCreditInputProps) {
  const [userAPCredits, setUserAPCredits] = useState<UserAPCredit[]>([]);
  const [changed, setChanged] = useState<boolean>(false);
  const [selectedExam, setSelectedExam] = useState<string>('');
  const [score, setScore] = useState<number>(5); // Changed default to 5
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const lastEmittedJsonRef = useRef<string>('');

  // Load AP credit data with error handling and memoization
  const allExams = useMemo(() => {
    try {
      const exams = getAllAPExams();
      console.log('Loaded AP exams:', exams.length, 'exams');
      if (exams.length > 0) {
        console.log('Sample exams:', exams.slice(0, 3).map(e => e.name));
      }
      return exams;
    } catch (error) {
      console.error('Error loading AP exams:', error);
      return [];
    }
  }, []);
  
  // Optimized search filtering with debouncing
  const searchFilteredExams = useMemo(() => {
    if (!searchQuery.trim()) {
      return allExams.slice(0, 8); // Show popular exams when no search query
    }
    
    try {
      const query = searchQuery.toLowerCase().trim();
      const filtered = allExams.filter(exam =>
        exam.name.toLowerCase().includes(query)
      );
      console.log('Search query:', searchQuery, 'Filtered results:', filtered.length);
      return filtered;
    } catch (error) {
      console.error('Error filtering exams by search:', error);
      return [];
    }
  }, [allExams, searchQuery]);

  // Memoized callback to prevent unnecessary re-renders
  const handleAPCreditsChange = useCallback((newCredits: UserAPCredit[]) => {
    onAPCreditsChange(newCredits);
    setChanged(false);
  }, [onAPCreditsChange]);

  useEffect(() => {
    // Only notify parent when the value actually changed to avoid update loops
    const json = JSON.stringify(userAPCredits);
    const initialJson = JSON.stringify(initialAPCredits);

    console.log('userAPCredits', userAPCredits);
    console.log('initialAPCredits', initialAPCredits);

    console.log('emit', json !== lastEmittedJsonRef.current && json !== initialJson);
    console.log('lastEmittedJsonRef.current', lastEmittedJsonRef.current);
    console.log('initialJson', initialJson);
    console.log('json', json);
    
    // Don't emit if it matches either the last emit or the initial credits
    if (changed) {
      console.log('AP credits changed, emitting update:', {
        currentCredits: userAPCredits.length,
        lastEmittedRef: lastEmittedJsonRef.current ? JSON.parse(lastEmittedJsonRef.current).length : 0,
        initialCredits: initialAPCredits?.length || 0
      });
      
      lastEmittedJsonRef.current = json;
      handleAPCreditsChange(userAPCredits);
    }
  }, [userAPCredits, handleAPCreditsChange, initialAPCredits]);

  // Hydrate from saved AP credits when provided
  useEffect(() => {
    if (!initialAPCredits) return;
    const incomingJson = JSON.stringify(initialAPCredits);
    const currentJson = JSON.stringify(userAPCredits);
    if (incomingJson !== currentJson) {
      setUserAPCredits(initialAPCredits);
      lastEmittedJsonRef.current = incomingJson;
    }
  }, [initialAPCredits]);

  // Helper function to convert AP credit equivalent to RutgersCourse
  const convertEquivalentToCourse = useCallback((equivalent: APCreditEquivalent): RutgersCourse => {
    const courseNameParts = equivalent.courseName.split(' ');
    const department = courseNameParts[0] || 'AP';
    const classNumber = courseNameParts.slice(1).join(' ') || 'Credit';
    
    return {
      id: equivalent.rutgersCourse.replace(/[^0-9]/g, ''),
      baseId: equivalent.rutgersCourse.replace(/[^0-9]/g, ''),
      courseName: equivalent.courseName,
      fullTitle: equivalent.courseName,
      department: department,
      class_number: classNumber,
      credits: equivalent.credits,
      campus: 'New Brunswick',
      meetingTimes: [],
      instructors: [],
      section: '01',
      subjectNotes: null,
      coreRequirements: [],
      prerequisites: undefined
    };
  }, []);

  const handleAddAPCredit = useCallback(() => {
    try {
      if (!selectedExam) return;

      const exam = getAPExamById(selectedExam);
      if (!exam) {
        console.error('Exam not found:', selectedExam);
        return;
      }

      // Check if this exam is already added
      const existingCredit = userAPCredits.find(credit => credit.examId === selectedExam);
      console.log('existingCredit', existingCredit);
      if (existingCredit) {
        // If it exists but with a different score, update it
        if (existingCredit.score !== score) {
          const applicableEquivalents = exam.rutgersEquivalents.filter(
            equivalent => score >= equivalent.minScore && score <= equivalent.maxScore
          );
          
          setUserAPCredits(prev => prev.map(credit => 
            credit.examId === selectedExam 
              ? {
                  ...credit,
                  score,
                  rutgersEquivalents: applicableEquivalents,
                }
              : credit
          ));
        }
        setSelectedExam('');
        setScore(5);
        setSearchQuery('');
        setIsSearchFocused(false);
        setChanged(true);
        return;
      }

      // Get applicable Rutgers equivalents based on score
      const applicableEquivalents = exam.rutgersEquivalents.filter(
        equivalent => score >= equivalent.minScore && score <= equivalent.maxScore
      );

      const newAPCredit: UserAPCredit = {
        examId: selectedExam,
        examName: exam.name,
        score,
        rutgersEquivalents: applicableEquivalents,
        isApplied: true
      };

      console.log('Adding new AP credit:', newAPCredit);
      
      setUserAPCredits(prev => {
        const newCredits = [...prev, newAPCredit];
        // Update the lastEmittedJsonRef to prevent duplicate updates
        lastEmittedJsonRef.current = JSON.stringify(newCredits);
        return newCredits;
      });

      setSelectedExam('');
      setScore(5);
      setSearchQuery('');
      setIsSearchFocused(false);
      setChanged(true);
    } catch (error) {
      console.error('Error adding AP credit:', error);
    }
  }, [selectedExam, score, userAPCredits]);

  const handleRemoveAPCredit = useCallback((examId: string) => {
    setUserAPCredits(prev => prev.filter(credit => credit.examId !== examId));
    setChanged(true);
  }, []);

  const handleToggleApplied = useCallback((examId: string) => {
    setUserAPCredits(prev => prev.map(credit => 
      credit.examId === examId 
        ? { ...credit, isApplied: !credit.isApplied }
        : credit
    ));
    setChanged(true);
  }, []);

  const handleExamSelect = useCallback((exam: APExam) => {
    setSelectedExam(exam.id);
    setSearchQuery(exam.name);
    setIsSearchFocused(false);
  }, []);

  const handleInputBlur = useCallback(() => {
    // Use a small delay to allow click events to fire first
    setTimeout(() => {
      setIsSearchFocused(false);
    }, 150);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);

  const getTotalCredits = useCallback(() => {
    return userAPCredits
      .filter(credit => credit.isApplied)
      .reduce((total, credit) => {
        return total + credit.rutgersEquivalents.reduce((sum, equivalent) => 
          sum + equivalent.credits, 0
        );
      }, 0);
  }, [userAPCredits]);

  const getAppliedCredits = useCallback(() => {
    return userAPCredits.filter(credit => credit.isApplied);
  }, [userAPCredits]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-control p-6 shadow-e1">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center">
              <AcademicCapIcon className="h-6 w-6 text-red-500 mr-2" />
              AP Credit Input
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Add your AP exam scores to see how they count toward prerequisites
            </p>
          </div>
        </div>

        {/* Quick Add Form */}
        <div className="bg-slate-50 dark:bg-slate-700 rounded-control p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Exam Search */}
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Search AP Exam
              </label>
              <input
                type="text"
                placeholder="Type to search AP exams..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedExam('');
                  setIsSearchFocused(true);
                }}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              {isSearchFocused && searchQuery && searchFilteredExams.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-control shadow-e2 max-h-48 overflow-y-auto">
                  {searchFilteredExams.slice(0, 10).map((exam) => (
                    <div
                      key={exam.id}
                      className="p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600 border-b border-slate-200 dark:border-slate-600 last:border-b-0"
                      onClick={() => handleExamSelect(exam)}
                    >
                      <h4 className="font-medium text-slate-900 dark:text-white">
                        {exam.name}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {[exam.subject !== 'Other' ? exam.subject : '', exam.description].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {isSearchFocused && searchQuery && searchFilteredExams.length === 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-control shadow-e2 p-3">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No AP exams found matching "{searchQuery}"
                  </p>
                </div>
              )}
              {!searchQuery && isSearchFocused && allExams.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-control shadow-e2 max-h-96 overflow-y-auto">
                  <div className="p-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-600">
                    AP Exams (click to select):
                  </div>
                  {allExams.map((exam) => (
                    <div
                      key={exam.id}
                      className="p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600 border-b border-slate-200 dark:border-slate-600 last:border-b-0"
                      onClick={() => handleExamSelect(exam)}
                    >
                      <h4 className="font-medium text-slate-900 dark:text-white">
                        {exam.name}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {exam.subject !== 'Other' ? exam.subject : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Score Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                AP Score
              </label>
              <select
                value={score}
                onChange={(e) => setScore(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
            </div>

            {/* Add Button */}
            <div className="flex items-end">
              <button
                onClick={handleAddAPCredit}
                disabled={!selectedExam}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-control hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add AP Credit
              </button>
            </div>
          </div>

          {/* Preview */}
          {selectedExam && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-control border border-blue-200 dark:border-blue-700">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                Credit Preview
              </h4>
              {(() => {
                const exam = getAPExamById(selectedExam);
                if (!exam) return null;
                
                const applicableEquivalents = exam.rutgersEquivalents.filter(
                  equivalent => score >= equivalent.minScore && score <= equivalent.maxScore
                );

                return (
                  <div>
                    {applicableEquivalents.length > 0 ? (
                      <div>
                        <p className="text-sm text-green-600 dark:text-green-400 mb-2">
                          <FunctionalIcon name="complete" /> You would receive credit for:
                        </p>
                        {applicableEquivalents.map((equivalent, index) => (
                          <div key={index} className="text-sm text-slate-700 dark:text-slate-300">
                            • {equivalent.courseName} ({equivalent.credits} credits)
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        <FunctionalIcon name="missing" /> No credit available for this score
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-control p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Applied Credits
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {getAppliedCredits().length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-control p-4">
            <div className="flex items-center">
              <InformationCircleIcon className="h-6 w-6 text-blue-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Total Credits
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {getTotalCredits()}
                </p>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* Applied AP Credits */}
      {getAppliedCredits().length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-control shadow-e1">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              Applied AP Courses ({getAppliedCredits().length})
            </h3>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getAppliedCredits().map((credit) => (
                <div
                  key={credit.examId}
                  className="bg-green-50 dark:bg-green-900/20 rounded-control p-4 border border-green-200 dark:border-green-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 dark:text-white">
                        {credit.examName}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Score: {credit.score}/5
                      </p>
                      <div className="mt-2">
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                          Rutgers Equivalents:
                        </p>
                        {credit.rutgersEquivalents.map((equivalent, index) => (
                          <p key={index} className="text-xs text-green-600 dark:text-green-400">
                            • {equivalent.courseName} ({equivalent.credits} credits)
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleApplied(credit.examId)}
                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveAPCredit(credit.examId)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 